import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Loader2 } from 'lucide-react';
import { 
  fetchRules, 
  AnnouncementRule 
} from '../../services/announcementRuleService';
import AnnouncementRulesList from '../../components/system/AnnouncementRulesList';
import AnnouncementRuleDialog from '../../components/system/AnnouncementRuleDialog';
import websocketService from '../../services/websocketService';

const AnnouncementRules = () => {
  const [rules, setRules] = useState<AnnouncementRule[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedRule, setSelectedRule] = useState<AnnouncementRule | null>(null);

  const loadRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const rulesData = await fetchRules();
      setRules(rulesData);
    } catch (err) {
      setError('Duyuru engelleme kuralları yüklenirken bir hata oluştu');
      console.error('Kurallar yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();

    // WebSocket olaylarını dinle
    const unsubscribe = websocketService.on('announcement_rule_update', (_) => {
      // WebSocket mesajını aldıktan sonra kuralları yeniden yükle
      loadRules();
    });

    return () => {
      // Temizlik
      unsubscribe();
    };
  }, []);

  const handleAddRule = () => {
    setSelectedRule(null);
    setOpenDialog(true);
  };

  const handleEditRule = (rule: AnnouncementRule) => {
    setSelectedRule(rule);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaved = () => {
    loadRules();
    setOpenDialog(false);
  };

  return (
    <div className="container mx-auto px-4">
      <div className="my-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            Duyuru Engelleme Kuralları
          </h1>
          <Button 
            onClick={handleAddRule}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Yeni Kural Ekle
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            <p className="text-gray-700 mb-4">
              Bu sayfada duyuru gönderimine engel olan zaman aralıklarını (tatil günleri, özel günler vb.) yönetebilirsiniz.
              Aktif bir engelleme kuralı olduğunda, yöneticiler o zaman aralığında yeni duyuru oluşturamazlar.
            </p>

            {error && (
              <Alert variant="destructive" className="my-4">
                <AlertDescription>
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="flex justify-center my-8">
                <Loader2 className="animate-spin" size={32} />
              </div>
            ) : (
              <AnnouncementRulesList 
                rules={rules} 
                onEdit={handleEditRule} 
                onRefresh={loadRules}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <AnnouncementRuleDialog
        open={openDialog}
        rule={selectedRule}
        onClose={handleCloseDialog}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default AnnouncementRules;
